import {getEnvironment} from '@sail/utils';
import {
  BATCH_QA_R_URL,
  BATCH_R_URL,
  METRICS_QA_R_URL,
  METRICS_R_URL,
} from 'src/internal/metrics/endpoints';

import type {ServiceName} from 'src/internal/common/types';
import type {MetricEvent} from 'src/internal/metrics/types';

const LEGACY_BATCH_SIZE = 25;
const AEL_METRICS_BATCH_SIZE = 200;

export type BatchMetricsTransportSettings = {
  getUrl: () => string;
  serializeBatchBody: (service: ServiceName, events: MetricEvent[]) => string;
  contentType: string;
  batchSize: number;
};

function flattenMetricEvent(event: MetricEvent): Record<string, string> {
  return {
    ...event.params,
    event_name: event.name,
    ...event.meta,
  };
}

function getLegacyBatchPayload(
  service: ServiceName,
  events: MetricEvent[],
): {
  client_id: string;
  num_requests: number;
  events: Record<string, string>[];
} {
  return {
    client_id: service,
    num_requests: events.length,
    events: events.map(flattenMetricEvent),
  };
}

function toQueryString(params: Record<string, unknown>): string {
  let qs = '';

  for (const key in params) {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      let value: unknown = params[key];

      if (typeof value === 'object') {
        value = JSON.stringify(value);
      }

      qs += `&${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
    }
  }

  return qs.replace('&', '');
}

function serializeLegacyBatchBody(
  service: ServiceName,
  events: MetricEvent[],
): string {
  return toQueryString(getLegacyBatchPayload(service, events));
}

function serializeAelMetricsBatchBody(
  service: ServiceName,
  events: MetricEvent[],
): string {
  return JSON.stringify({
    client_id: service,
    events: events.map((event) => ({
      event_name: event.name,
      event_id: event.meta.event_id,
      created: Number(event.meta.created),
      params: {
        ...event.params,
        ...(event.meta.page_view_id
          ? {page_view_id: event.meta.page_view_id}
          : {}),
      },
    })),
  });
}

function getLegacyBatchMetricsTransportSettings(): BatchMetricsTransportSettings {
  return {
    getUrl: () =>
      getEnvironment() === 'production' ? BATCH_R_URL : BATCH_QA_R_URL,
    serializeBatchBody: serializeLegacyBatchBody,
    contentType: 'application/x-www-form-urlencoded',
    batchSize: LEGACY_BATCH_SIZE,
  };
}

function getAelMetricsBatchTransportSettings(): BatchMetricsTransportSettings {
  return {
    getUrl: () =>
      getEnvironment() === 'production' ? METRICS_R_URL : METRICS_QA_R_URL,
    serializeBatchBody: serializeAelMetricsBatchBody,
    contentType: 'application/json',
    batchSize: AEL_METRICS_BATCH_SIZE,
  };
}

export default function getBatchMetricsTransportSettings(
  useAelMetricsEndpoint?: boolean,
): BatchMetricsTransportSettings {
  if (useAelMetricsEndpoint) {
    return getAelMetricsBatchTransportSettings();
  }

  return getLegacyBatchMetricsTransportSettings();
}
